/**
 * haiku-theme.js — homepage slider photo-fit + storefront a11y touch-ups.
 *
 * Part 1 generalizes the proven product-gallery-fit approach to the
 * vertical slider: the theme sizes slider media by grid-width columns and
 * derives height from the padding-top aspect hack (lazy-image.liquid), so
 * tall portraits overflow the viewport. CSS can't read the per-image
 * data-ratio, so we cap each figure's WIDTH such that
 *
 *   height = width / ratio   →   width = (viewportHeight − gutter) × ratio
 *
 * We constrain the .lazy-image figure (and the .slide-image__video wrapper),
 * never the grid column, so the slider's layout maths and the lazyanimation
 * clip-path reveal are untouched. A conservative CSS-only fallback lives in
 * haiku-theme.css for the no-JS case.
 *
 * Part 2 fixes small keyboard/AT defects that can't be reached with CSS:
 * disabled pagination arrows that are still tabbable, a dangling
 * aria-describedby on the sort select, a tabindex="-1" on the search submit,
 * and the positive tabindex antipattern on the skip link / header controls.
 */
(function () {
  'use strict';

  var cfg = window.haikuThemeConfig || {};
  var GUTTER = cfg.gutter || 140; // px reserved for header + breathing room
  var MIN_WIDTH = cfg.minWidth || 260; // never shrink narrower than this
  var MIN_VIEWPORT = cfg.minViewport || 1024; // below this, leave the theme alone

  /* --------------------------------------------------------------------
     1. Photo fit — vertical slider
  -------------------------------------------------------------------- */

  var SELECTOR =
    '.vertical-slider .slide-image .lazy-image[data-ratio], ' +
    '.vertical-slider .slide-image .slide-image__video';
  var selfDispatched = false;

  function ratioOf(el) {
    var r = parseFloat(el.getAttribute('data-ratio'));
    if (r && isFinite(r) && r > 0) return r;
    // video wrappers carry the ratio as an inline padding-top percentage
    var pt = parseFloat(el.style.paddingTop);
    if (pt && isFinite(pt) && pt > 0) return 100 / pt;
    return 0;
  }

  function clear(items) {
    Array.prototype.forEach.call(items, function (el) {
      el.style.maxWidth = '';
      el.style.marginLeft = '';
      el.style.marginRight = '';
    });
  }

  function fit() {
    var items = document.querySelectorAll(SELECTOR);
    if (!items.length) return;

    // Palm stays full-bleed and lap keeps the theme's own layout.
    if (window.innerWidth < MIN_VIEWPORT) {
      clear(items);
      return;
    }

    var maxHeight = Math.max(320, window.innerHeight - GUTTER);
    var changed = false;

    Array.prototype.forEach.call(items, function (el) {
      var ratio = ratioOf(el);
      if (!ratio) return;

      var width = Math.max(MIN_WIDTH, Math.round(maxHeight * ratio));
      var next = width + 'px';
      if (el.style.maxWidth !== next) {
        el.style.maxWidth = next;
        el.style.marginLeft = 'auto';
        el.style.marginRight = 'auto';
        changed = true;
      }
    });

    // The slider re-measures rotated caption widths on resize
    // (component-slider-vertical.js _resizeCaptions), so nudge it once.
    // Guarded so our own resize listener doesn't recurse.
    if (changed) {
      selfDispatched = true;
      window.dispatchEvent(new Event('resize'));
      selfDispatched = false;
    }
  }

  /* --------------------------------------------------------------------
     2. a11y touch-ups
  -------------------------------------------------------------------- */

  function a11y() {
    // Disabled pagination arrows render <a href=""> — pointer-events:none
    // stops clicks but not the keyboard. Take them out of the tab order.
    Array.prototype.forEach.call(
      document.querySelectorAll('.pagination li.disabled a'),
      function (a) {
        a.setAttribute('tabindex', '-1');
        a.setAttribute('aria-hidden', 'true');
      }
    );

    // The sort select points aria-describedby at an element the theme
    // never renders; drop the dangling reference (only if truly absent).
    if (!document.getElementById('a11y-refresh-page-message')) {
      Array.prototype.forEach.call(
        document.querySelectorAll('[aria-describedby="a11y-refresh-page-message"]'),
        function (el) {
          el.removeAttribute('aria-describedby');
        }
      );
    }

    // The search submit button carries tabindex="-1", hiding it from
    // keyboard users. Enter still submits, but the button should be real.
    Array.prototype.forEach.call(
      document.querySelectorAll('.search-form button[type="submit"][tabindex="-1"]'),
      function (btn) {
        btn.removeAttribute('tabindex');
      }
    );

    // Positive tabindex antipattern: skip link (1), menu opener (2),
    // logo (3) hijack the global tab order. Their DOM order is already
    // correct, so the attributes can simply go.
    ['.skip-to-content[tabindex="1"]', '#menu-open-button[tabindex="2"]', '.header .logo-img[tabindex="3"]']
      .forEach(function (sel) {
        var el = document.querySelector(sel);
        if (el) el.removeAttribute('tabindex');
      });
  }

  /* --------------------------------------------------------------------
     wiring
  -------------------------------------------------------------------- */

  var timer = null;
  window.addEventListener('resize', function () {
    if (selfDispatched) return;
    clearTimeout(timer);
    timer = setTimeout(fit, 150);
  });

  function init() {
    fit();
    a11y();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Media can arrive late and the theme editor re-renders sections in place.
  window.addEventListener('load', fit);
  document.addEventListener('shopify:section:load', init);
})();
